import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'agphub_consent_status';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setShow(true);
    } else {
      try {
        const { status, timestamp } = JSON.parse(consent);
        const sixMonths = 6 * 30 * 24 * 60 * 60 * 1000;
        
        // If expired, show banner again
        if (Date.now() - timestamp > sixMonths) {
          setShow(true);
        } else if (status === 'granted') {
          // If already granted and not expired, update consent to granted
          updateConsent('granted');
        }
      } catch (e) {
        // If error parsing, show banner
        setShow(true);
      }
    }
  }, []);

  const updateConsent = (status: 'granted' | 'denied') => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': status,
        'ad_storage': status,
        'ad_user_data': status,
        'ad_personalization': status,
      });
    }
  };

  const handleAcceptAll = () => {
    updateConsent('granted');
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ 
      status: 'granted', 
      timestamp: Date.now() 
    }));
    setShow(false);
  };

  const handleDecline = () => {
    updateConsent('denied');
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ 
      status: 'denied', 
      timestamp: Date.now() 
    }));
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      >
        <div className="max-w-7xl mx-auto bg-[#0b111d] border border-gray-800 shadow-2xl rounded-xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="text-white text-lg font-semibold mb-2">Control de Privacidad</h3>
              <p className="text-[#b0b8c6] text-sm leading-relaxed">
                Utilizamos cookies propias y de terceros para fines analíticos y para mostrarte publicidad personalizada en base a un perfil elaborado a partir de tus hábitos de navegación. Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <Link 
                to="/cookies"
                className="text-xs text-[#b0b8c6] hover:text-white underline transition-colors mr-2 pt-2 md:pt-0"
              >
                Configurar preferencias
              </Link>
              
              <button
                onClick={handleDecline}
                className="flex-1 md:flex-none px-6 py-2.5 text-sm font-medium text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-all cursor-pointer"
              >
                Rechazar no esenciales
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none px-6 py-2.5 text-sm font-medium text-white bg-[#28a745] hover:bg-[#218838] rounded-lg transition-all shadow-lg shadow-green-900/20 cursor-pointer"
              >
                Aceptar todo
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
